import { useEffect, useState } from 'react';
import { Button, Field, Header, Input, Message, Modal, Toast } from 'react-frontier';
import { bindFormChange, formatFullAddress, formatPlacesTemp } from '../SiteUtil';
import { SetLoading, Location, InventoryWarehouse } from '../SiteClasses';
import Validator from '../Validator';
import moment from 'moment';
import API from '../API';
import Autocomplete from './Autocomplete';

interface ModalProps{
   open: boolean,
   onClose: ()=> void,
   onCreate?: (warehouse: InventoryWarehouse)=> void,
   onUpdate?: (warehouse: InventoryWarehouse)=> void,
   warehouseId?: number,
}

var CreateWarehouseModal = (props: ModalProps)=>{
   var [prompts, setPrompts] = useState<string[]>(null);
   var [form, setForm] = useState<Partial<InventoryWarehouse>>(null);
   var [loading, setLoadingState] = useState<boolean>(false);
   var [location, setLocation] = useState<Location>(null);
   var [locationQuery, setLocationQuery] = useState('');

   useEffect(()=>{
      if(!props.open) return;
      setPrompts(null);
      setLocation(null);
      setLocationQuery('');

      if(props.warehouseId){
         setLoadingState(true);
         API.getWarehouse(props.warehouseId).then(res=>{
            if(res.error) return setPrompts([res.message]);
            setForm({
               warehouse_name: res.data.warehouse_name,
               description: res.data.description,
               capacity: res.data.capacity
            });
         }).catch(()=>{
            setPrompts(['Hubo un error cargando la información del almacén.']);
         }).finally(()=>{
            setLoadingState(false);
         });
      }else{
         setForm({});
      }
   }, [props.open, props.warehouseId]);

   var submit = (setLoading: SetLoading)=>{
      if(!form) return;
      var { valid, prompts } = Validator({ ...form, location }, {
         warehouse_name: [{
            rule: 'length', params: [3, 64], label: 'Nombre'
         }],
         description: [{
            rule: 'length', params: [3, 128], label: 'Descripción'
         }],
         capacity: [{ rule: 'number', label: 'Capacidad máxima' }],
         ...(!props.warehouseId ? {
            location: [{
               rule: (val: Location)=>
                  !!val &&
                  !!val.street &&
                  !!val.exterior_number &&
                  !!val.neighborhood &&
                  !!val.city &&
                  !!val.state &&
                  val.lattitude != null &&
                  val.longitude != null,
               prompt: 'Seleccione una ubicación válida.',
            }],
         } : {}),
      });
      setPrompts(prompts);
      if(!valid) return;
      setLoading(true);

      if(props.warehouseId){
         API.editInventoryWarehouse(props.warehouseId, form.capacity, form.warehouse_name, form.description).then(res=>{
            if(res.error) return setPrompts([res.message]);
            Toast.success('Almacén actualizado!');

            props.onUpdate?.({
               warehouse_name: form.warehouse_name,
               description: form.description,
               capacity: form.capacity
            } as InventoryWarehouse);
            props.onClose();
         }).catch(err=>{
            setPrompts(['Hubo un error inesperado actualizando el almacén (LCL-ECM-2)']);
         }).finally(()=>{
            setLoading(false);
         });
      }else{
         API.createInventoryWarehouse(form.warehouse_name, form.capacity, form.description, location).then(res=>{
            if(res.error) return setPrompts([res.message]);
            Toast.success('Almacén creado!');

            props.onCreate?.({
               warehouse_id: res.data.warehouse_id,
               warehouse_name: form.warehouse_name,
               description: form.description,
               location_city: location?.city,
               date_created: moment().unix(),
            } as InventoryWarehouse);
            props.onClose();
         }).catch(err=>{
            setPrompts(['Hubo un error inesperado creando el almacén (INV-CRT-1)']);
         }).finally(()=>{
            setLoading(false);
         });
      }
   }

   var onFormChange = bindFormChange(form, setForm);

   return (
      <Modal open={props.open} onClose={props.onClose} size='tiny'>
         <Modal.Header>
            {props.warehouseId ? 'Editar almacén' : 'Crear almacén'}
         </Modal.Header>
         <Modal.Content style={{ paddingTop: 10, paddingBottom: 5 }}>
            {loading ? (
               <Header loading text='Cargando' />
            ) : !form ? (
               <Header text='Error cargando datos' />
            ) : (
               <>
                  <Input
                     label='Nombre'
                     placeholder='Nombre del almacén'
                     value={form.warehouse_name}
                     onChange={onFormChange('warehouse_name')}
                  />
                  <Input
                     label='Descripción'
                     placeholder='Descripción del almacén'
                     textarea
                     value={form.description}
                     onChange={onFormChange('description')}
                  />
                  <Input 
                     value={form.capacity}
                     label='Capacidad máxima'
                     onChange={onFormChange('capacity')}
                     placeholder='Camacidad máxima del almacén'
                  />
                  {!props.warehouseId && (
                     <>
                        <Field label='Ubicación'>
                           <Autocomplete
                              placeholder='Busca una dirección'
                              value={locationQuery}
                              onChange={(eOrVal)=> setLocationQuery(eOrVal?.target ? eOrVal.target.value : eOrVal)}
                              onPlaceSelected={(loc)=>{
                                 var formatted = formatPlacesTemp(loc);
                                 setLocationQuery(formatFullAddress(formatted));
                                 setLocation(formatted);
                              }}
                           />
                        </Field>
                        {location && (
                           <>
                              <Input
                                 label="Calle"
                                 value={location?.street ?? ''}
                                 onChange={(value) => setLocation(prev=>({ ...prev, street: value?.target ? value.target.value : value }))}
                              />
                              <Field style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
                                 <Input
                                    label="Número exterior"
                                    value={location?.exterior_number ?? ''}
                                    onChange={(value) => setLocation(prev=>({ ...prev, exterior_number: value?.target ? value.target.value : value }))}
                                 />
                                 <Input
                                    label="Número interior"
                                    value={location?.interior_number ?? ''}
                                    onChange={(value) => setLocation(prev=>({ ...prev, interior_number: value?.target ? value.target.value : value }))}
                                 />
                              </Field>
                              <Field style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
                                 <Input
                                    label="Colonia"
                                    value={location?.neighborhood ?? ''}
                                    onChange={(value) => setLocation(prev=>({ ...prev, neighborhood: value?.target ? value.target.value : value }))}
                                 />
                                 <Input
                                    label="Ciudad"
                                    value={location?.city ?? ''}
                                    onChange={(value) => setLocation(prev=>({ ...prev, city: value?.target ? value.target.value : value }))}
                                 />
                              </Field>
                              <Field style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
                                 <Input
                                    label="Código Postal"
                                    value={location?.zipcode ?? ''}
                                    onChange={(value) => setLocation(prev=>({ ...prev, zipcode: value?.target ? value.target.value : value }))}
                                 />
                                 <Input
                                    label="Estado"
                                    value={location?.state ?? ''}
                                    onChange={(value) => setLocation(prev=>({ ...prev, state: value?.target ? value.target.value : value }))}
                                 />
                              </Field>
                           </>
                        )}
                     </>
                  )}
                  <Message style={{ marginTop: 15, marginBottom: 15 }} type='error' list={prompts} />
               </>
            )}
         </Modal.Content>
         <Modal.Actions>
            <Button basic text='Cerrar' onClick={props.onClose} />
            <Button color='black' text='Guardar' onClick={submit} />
         </Modal.Actions>
      </Modal>
   )
}

export default CreateWarehouseModal;
